import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

interface Lesson {
  id: string;
  title: string;
  category: string;
  duration: string;
  icon: string;
  color: string;
  description: string;
  content: {
    intro: string;
    keyPoints: string[];
    examples: { situation: string; response: string }[];
    takeaway: string;
  };
}

export default function Learning() {
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  const lessons: Lesson[] = [
    {
      id: '1',
      title: 'Setting Healthy Boundaries',
      category: 'Boundaries',
      duration: '5 min',
      icon: 'ri-shield-user-line',
      color: 'blue',
      description: 'Learn how to communicate your limits clearly and respectfully',
      content: {
        intro: 'Boundaries are like invisible lines that protect your time, energy, and emotional well-being. They help you feel safe and respected in all your relationships.',
        keyPoints: [
          'Boundaries are not mean or selfish—they\'re healthy and necessary',
          'You have the right to say no without explaining yourself',
          'Good friends will respect your boundaries',
          'It\'s okay to change your boundaries as you grow',
          'Boundaries protect both you and your relationships'
        ],
        examples: [
          {
            situation: 'Friend asks you to hang out but you need study time',
            response: '"I\'d love to hang out, but I need to focus on studying tonight. Can we plan for this weekend instead?"'
          },
          {
            situation: 'Someone keeps texting late at night',
            response: '"Hey, I need to sleep by 10pm on school nights. Can we chat earlier in the day?"'
          },
          {
            situation: 'Friend shares too much personal information',
            response: '"I care about you, but I\'m not comfortable discussing this. Maybe you could talk to a counselor?"'
          }
        ],
        takeaway: 'Setting boundaries gets easier with practice. Start small, be clear, and remember that people who truly care about you will respect your limits.'
      }
    },
    {
      id: '2',
      title: 'Understanding Emotional Cues',
      category: 'Emotions',
      duration: '6 min',
      icon: 'ri-emotion-line',
      color: 'green',
      description: 'Recognize emotional cues in messages and respond with empathy',
      content: {
        intro: 'Text messages can be tricky because we can\'t see facial expressions or hear tone of voice. Learning to read emotional cues helps you understand what someone is really feeling.',
        keyPoints: [
          'Look for words that express feelings (sad, excited, frustrated)',
          'Notice punctuation—lots of exclamation points or all caps can signal strong emotions',
          'Pay attention to response time and message length changes',
          'Consider what\'s happening in their life right now',
          'When in doubt, ask clarifying questions'
        ],
        examples: [
          {
            situation: 'Message: "I\'m fine. Whatever."',
            response: 'Likely feeling hurt or upset. Try: "It seems like something\'s bothering you. Want to talk about it?"'
          },
          {
            situation: 'Message: "OMG I CAN\'T BELIEVE IT!!!"',
            response: 'Very excited or shocked. Match their energy: "That\'s amazing! Tell me everything!"'
          },
          {
            situation: 'Message: "Yeah... I guess..."',
            response: 'Uncertain or disappointed. Check in: "You don\'t sound sure. What\'s on your mind?"'
          }
        ],
        takeaway: 'Emotional intelligence in texting takes practice. The more you pay attention to these cues, the better you\'ll become at understanding and responding to others\' feelings.'
      }
    },
    {
      id: '3',
      title: 'Spotting Red Flags',
      category: 'Safety',
      duration: '7 min',
      icon: 'ri-flag-line',
      color: 'red',
      description: 'Identify unhealthy patterns like manipulation or pressure',
      content: {
        intro: 'Red flags are warning signs that someone might not be treating you with the respect you deserve. Learning to spot them early helps you protect yourself.',
        keyPoints: [
          'Guilt-tripping: Making you feel bad for normal boundaries',
          'Pressure: Pushing you to do things you\'re uncomfortable with',
          'Isolation: Trying to separate you from friends and family',
          'Jealousy: Getting upset about your other relationships',
          'Gaslighting: Making you doubt your own feelings or memory'
        ],
        examples: [
          {
            situation: '"If you really cared about me, you\'d..."',
            response: 'Red flag: Guilt-tripping. Healthy response: "I do care, but I\'m not comfortable with that."'
          },
          {
            situation: '"Why are you hanging out with them? I thought I was enough for you."',
            response: 'Red flag: Isolation and jealousy. Healthy response: "I value all my friendships. That\'s not going to change."'
          },
          {
            situation: '"You\'re being too sensitive. I was just joking."',
            response: 'Red flag: Gaslighting. Healthy response: "My feelings are valid. Please don\'t dismiss them."'
          }
        ],
        takeaway: 'Trust your gut. If something feels wrong, it probably is. Talk to a trusted adult if you\'re seeing multiple red flags in a relationship.'
      }
    },
    {
      id: '4',
      title: 'Building Healthy Relationships',
      category: 'Relationships',
      duration: '6 min',
      icon: 'ri-heart-line',
      color: 'purple',
      description: 'Build strong friendships based on respect and trust',
      content: {
        intro: 'Healthy relationships—whether friendships or romantic—are built on mutual respect, trust, and support. They make you feel good about yourself.',
        keyPoints: [
          'Both people feel heard and valued',
          'You can be yourself without fear of judgment',
          'Conflicts are resolved through calm discussion',
          'You support each other\'s goals and friendships',
          'There\'s a balance of give and take'
        ],
        examples: [
          {
            situation: 'Healthy: "I\'m proud of you for making the team!"',
            response: 'Shows genuine support for your achievements'
          },
          {
            situation: 'Healthy: "I disagree, but I understand where you\'re coming from."',
            response: 'Respects different opinions without getting defensive'
          },
          {
            situation: 'Healthy: "I messed up. I\'m sorry. How can I make it right?"',
            response: 'Takes responsibility and wants to fix mistakes'
          }
        ],
        takeaway: 'Healthy relationships take work from both people. They should add to your life, not drain your energy or make you feel bad about yourself.'
      }
    },
    {
      id: '5',
      title: 'Effective Communication',
      category: 'Communication',
      duration: '5 min',
      icon: 'ri-chat-3-line',
      color: 'yellow',
      description: 'Express yourself clearly and listen actively',
      content: {
        intro: 'Good communication is about expressing yourself clearly while also listening to understand, not just to respond.',
        keyPoints: [
          'Use "I" statements to express feelings without blaming',
          'Be specific about what you need or want',
          'Listen actively—put your phone down and focus',
          'Ask questions to make sure you understand',
          'Take breaks if conversations get too heated'
        ],
        examples: [
          {
            situation: 'Instead of: "You never listen to me!"',
            response: 'Try: "I feel unheard when I\'m talking and you\'re on your phone."'
          },
          {
            situation: 'Instead of: "You\'re so annoying!"',
            response: 'Try: "I need some alone time right now. Can we talk later?"'
          },
          {
            situation: 'Instead of: "Fine, whatever."',
            response: 'Try: "I\'m not okay with this, but I need time to think about how to explain why."'
          }
        ],
        takeaway: 'Clear communication prevents misunderstandings and helps build stronger relationships. It\'s a skill that improves with practice.'
      }
    },
    {
      id: '6',
      title: 'Dealing with Peer Pressure',
      category: 'Boundaries',
      duration: '6 min',
      icon: 'ri-group-line',
      color: 'orange',
      description: 'Stand firm in your values when facing pressure from peers',
      content: {
        intro: 'Peer pressure can be direct or subtle, but either way, it\'s important to stay true to yourself and your values.',
        keyPoints: [
          'Real friends respect your choices',
          'You don\'t need to explain or justify your decisions',
          'It\'s okay to remove yourself from uncomfortable situations',
          'Having a prepared response can help in the moment',
          'Your safety and comfort matter more than fitting in'
        ],
        examples: [
          {
            situation: '"Everyone\'s doing it. Don\'t be lame."',
            response: '"I\'m good, thanks. I\'m not interested." Then change the subject or leave.'
          },
          {
            situation: '"Just this once won\'t hurt."',
            response: '"I said no. Please respect that." You don\'t owe more explanation.'
          },
          {
            situation: '"If you don\'t do this, we can\'t be friends."',
            response: '"Then I guess we have different ideas about friendship." Real friends don\'t give ultimatums.'
          }
        ],
        takeaway: 'Standing up to peer pressure gets easier each time you do it. Your future self will thank you for staying true to your values.'
      }
    }
  ];

  const categories = ['All', 'Boundaries', 'Emotions', 'Safety', 'Relationships', 'Communication'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredLessons = activeCategory === 'All' 
    ? lessons 
    : lessons.filter(lesson => lesson.category === activeCategory);

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; hover: string }> = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'hover:bg-blue-50' },
      green: { bg: 'bg-green-100', text: 'text-green-600', hover: 'hover:bg-green-50' },
      red: { bg: 'bg-red-100', text: 'text-red-600', hover: 'hover:bg-red-50' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-50' },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', hover: 'hover:bg-yellow-50' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', hover: 'hover:bg-orange-50' }
    };
    return colors[color] || colors.blue;
  };

  if (selectedLesson) {
    const colorClasses = getColorClasses(selectedLesson.color);
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <Navigation />
        
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => setSelectedLesson(null)}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 mb-8 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to Lessons</span>
            </button>

            {/* Lesson Header */}
            <div className={`${colorClasses.bg} rounded-2xl p-8 mb-8`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-16 h-16 bg-white rounded-2xl flex items-center justify-center`}>
                  <i className={`${selectedLesson.icon} text-3xl ${colorClasses.text}`}></i>
                </div>
                <span className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700">
                  {selectedLesson.duration}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedLesson.title}</h1>
              <p className="text-base text-gray-700">{selectedLesson.description}</p>
            </div>

            {/* Lesson Content */}
            <div className="space-y-8">
              {/* Introduction */}
              <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <p className="text-base text-gray-700 leading-relaxed">{selectedLesson.content.intro}</p>
              </div>

              {/* Key Points */}
              <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Key Points to Remember</h2>
                <ul className="space-y-4">
                  {selectedLesson.content.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className={`w-6 h-6 ${colorClasses.bg} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <i className={`ri-check-line text-sm ${colorClasses.text}`}></i>
                      </div>
                      <span className="text-sm text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Examples */}
              <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Real-Life Examples</h2>
                <div className="space-y-6">
                  {selectedLesson.content.examples.map((example, index) => (
                    <div key={index} className="border-l-4 border-gray-200 pl-6">
                      <p className="text-sm font-semibold text-gray-900 mb-2">{example.situation}</p>
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">{example.response}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Takeaway */}
              <div className={`${colorClasses.bg} rounded-2xl p-6 md:p-8`}>
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`ri-lightbulb-line text-2xl ${colorClasses.text}`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Key Takeaway</h3>
                    <p className="text-sm text-gray-700">{selectedLesson.content.takeaway}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <button
                  onClick={() => setSelectedLesson(null)}
                  className="px-6 py-3 bg-gray-100 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  Back to All Lessons
                </button>
                <Link
                  to="/analyzer"
                  className="px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                >
                  Try the Analyzer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Learning Center</h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Build your communication skills with bite-sized lessons on healthy relationships, boundaries, and emotional intelligence.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Lessons Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLessons.map((lesson) => {
              const colorClasses = getColorClasses(lesson.color);
              return (
                <div
                  key={lesson.id}
                  onClick={() => setSelectedLesson(lesson)}
                  className={`bg-white rounded-2xl shadow-sm p-6 ${colorClasses.hover} transition-all cursor-pointer hover:shadow-md`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 ${colorClasses.bg} rounded-xl flex items-center justify-center`}>
                      <i className={`${lesson.icon} text-2xl ${colorClasses.text}`}></i>
                    </div>
                    <span className="bg-gray-100 px-3 py-1 rounded-full text-xs font-medium text-gray-600">
                      {lesson.duration}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{lesson.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{lesson.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">{lesson.category}</span>
                    <i className={`ri-arrow-right-line ${colorClasses.text}`}></i>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Practice What You've Learned?
            </h2>
            <p className="text-base text-blue-100 mb-6 max-w-2xl mx-auto">
              Use our message analyzer to apply these skills to real conversations and get personalized reply suggestions.
            </p>
            <Link
              to="/analyzer"
              className="inline-block px-8 py-4 bg-white text-blue-600 text-base font-semibold rounded-full hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap"
            >
              Try the Analyzer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
